/**
 * Registration link states (PLAN §5). Derived, never stored: a link "opens" the
 * moment its opens_at passes, with nobody around to flip a flag — so the state
 * must be computed from the clock on every read.
 */
export const LINK_STATES = ['disabled', 'scheduled', 'open', 'closed', 'capped'] as const
export type LinkState = (typeof LINK_STATES)[number]

export const LINK_STATE_LABELS: Record<LinkState, string> = {
  disabled: 'Disabled',
  scheduled: 'Scheduled',
  open: 'Open',
  closed: 'Closed',
  capped: 'Quota full',
}

/**
 * Optional sections a link can hide (PLAN §5, "visible field set"). Identity,
 * contact and preferences are always shown — a registration without a name,
 * phone or destination isn't worth collecting.
 */
export const LINK_SECTIONS = [
  'passport',
  'education',
  'languages',
  'foreign_experience',
  'physical',
  'emergency',
] as const
export type LinkSection = (typeof LINK_SECTIONS)[number]

export const LINK_SECTION_LABELS: Record<LinkSection, string> = {
  passport: 'Passport details',
  education: 'Education & experience',
  languages: 'Languages',
  foreign_experience: 'Foreign work experience',
  physical: 'Height & weight',
  emergency: 'Emergency contact',
}

/** A new link collects the useful basics without becoming a wall of questions. */
export const DEFAULT_LINK_SECTIONS: readonly LinkSection[] = ['passport', 'education', 'languages']

export interface LinkStateInput {
  active: boolean
  opensAt: string | null
  closesAt: string | null
  maxRegistrations: number | null
  registrationCount: number
  now?: Date
}

/**
 * Single source of truth for link state — used by the public endpoint to accept
 * or refuse a submission, and by the staff UI to draw the status chip. Sharing
 * it is what stops the badge saying "Open" while the API refuses to accept.
 *
 * Order matters: an explicitly disabled link reads as disabled even if it's
 * also past its close date, because that's the more actionable fact.
 */
export function computeLinkState(input: LinkStateInput): LinkState {
  const now = input.now ?? new Date()

  if (!input.active) return 'disabled'
  if (input.opensAt && now < new Date(input.opensAt)) return 'scheduled'
  if (input.closesAt && now > new Date(input.closesAt)) return 'closed'
  if (input.maxRegistrations !== null && input.registrationCount >= input.maxRegistrations) {
    return 'capped'
  }
  return 'open'
}

export function isLinkAcceptingRegistrations(input: LinkStateInput): boolean {
  return computeLinkState(input) === 'open'
}
